import { useRef } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowLeft, ArrowRight, ArrowUpRight, Check } from "lucide-react";
import { useLanguage } from "@/i18n";
import { PROJECTS } from "@/data/projects";
import { Reveal } from "@/components/Reveal";
import ContactForm from "@/components/ContactForm";

const EASE = [0.22, 1, 0.36, 1];

export default function ProjectPage() {
  const { slug } = useParams();
  const { lang, t } = useLanguage();
  const idx = PROJECTS.findIndex((p) => p.slug === slug);
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);

  if (idx === -1) return <Navigate to={`/${lang}`} replace />;

  const p = PROJECTS[idx];
  const next = PROJECTS[(idx + 1) % PROJECTS.length];
  const c = p[lang];

  return (
    <main data-testid={`project-page-${p.slug}`}>
      <section ref={ref} className="relative h-[75svh] min-h-[520px] overflow-hidden bg-ebony">
        <motion.div style={{ y }} className="absolute inset-0 scale-110">
          <img src={p.image} alt={p.name} className="h-full w-full object-cover" />
        </motion.div>
        <div className="absolute inset-0 bg-black/45" />
        <div className="absolute inset-0 pattern-kitenge opacity-60" />
        <div className="relative z-10 h-full max-w-7xl mx-auto px-6 lg:px-12 flex flex-col justify-end pb-16 md:pb-24">
          <span className="block overflow-hidden mb-4">
            <motion.span
              className="block text-xs uppercase tracking-[0.3em] text-ochre"
              initial={{ y: "110%" }}
              animate={{ y: 0 }}
              transition={{ duration: 1, delay: 0.2, ease: EASE }}
            >
              {t.footer.venturesTitle} · {p.index} / 07
            </motion.span>
          </span>
          <h1 className="font-serif font-light text-bone text-5xl sm:text-6xl lg:text-8xl tracking-tighter" data-testid="project-title">
            <span className="block overflow-hidden">
              <motion.span className="block" initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ duration: 1.1, delay: 0.35, ease: EASE }}>
                {p.name}
              </motion.span>
            </span>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.7, ease: EASE }}
            className="mt-4 font-serif italic text-xl md:text-2xl text-bone/80"
            data-testid="project-tagline"
          >
            {c.tagline}
          </motion.p>
        </div>
      </section>

      <section className="bg-bone py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 grid md:grid-cols-12 gap-12 md:gap-20">
          <Reveal className="md:col-span-7">
            <Link
              to={`/${lang}/#ventures`}
              data-testid="project-back-link"
              className="group inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-ebony/50 hover:text-terracotta transition-colors duration-300"
            >
              <ArrowLeft size={14} className="transition-transform duration-300 group-hover:-translate-x-1" />
              {t.project.back}
            </Link>
            <p className="mt-10 font-serif text-2xl md:text-4xl font-light leading-snug tracking-tight text-ebony max-w-2xl" data-testid="project-description">
              {c.desc}
            </p>
          </Reveal>
          <Reveal className="md:col-span-5" delay={0.1}>
            <p className="text-xs uppercase tracking-[0.3em] text-terracotta mb-8">{t.project.featuresOverline}</p>
            <ul className="space-y-5">
              {c.features.map((f, i) => (
                <li key={i} className="flex items-start gap-4 border-b border-ebony/10 pb-5" data-testid={`project-feature-${i}`}>
                  <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-moss/15 text-moss">
                    <Check size={13} />
                  </span>
                  <span className="text-sm md:text-base font-light text-ebony/80">{f}</span>
                </li>
              ))}
            </ul>
            <a
              href="#contact"
              data-testid="project-enquire-button"
              className="group mt-10 inline-flex items-center gap-3 rounded-full bg-terracotta px-7 py-3.5 text-sm font-medium text-bone hover:bg-ochre hover:text-ebony transition-colors duration-500"
            >
              {t.project.cta}
              <ArrowRight size={16} className="transition-transform duration-500 group-hover:translate-x-1" />
            </a>
          </Reveal>
        </div>
      </section>

      {p.portfolio && (
        <section className="bg-bone border-t border-ebony/10 py-20 md:py-28" data-testid="portfolio-section">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <Reveal className="mb-12 md:mb-16">
              <h2 className="font-serif font-light text-3xl sm:text-4xl lg:text-5xl tracking-tighter text-ebony" data-testid="portfolio-title">
                {t.project.galleryTitle}
              </h2>
              <p className="mt-3 text-sm md:text-base font-light text-ebony/60 max-w-xl">{t.project.gallerySub}</p>
            </Reveal>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {p.portfolio.map((w, i) => {
                const Tag = w.url ? "a" : "div";
                const linkProps = w.url ? { href: w.url, target: "_blank", rel: "noopener noreferrer" } : {};
                return (
                <Reveal key={w.title} delay={0.05 * (i % 3)}>
                  <Tag {...linkProps} className="group relative block overflow-hidden rounded-md border border-ebony/10 h-72" data-testid={`portfolio-item-${i}`}>
                    <img
                      src={w.image}
                      alt={w.title}
                      loading="lazy"
                      className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-ebony/85 via-ebony/15 to-transparent" />
                    {w.url && (
                      <span className="absolute top-4 right-4 flex h-9 w-9 items-center justify-center rounded-full bg-bone/90 text-ebony opacity-0 transition-opacity duration-500 group-hover:opacity-100">
                        <ArrowUpRight size={15} />
                      </span>
                    )}
                    <div className="absolute bottom-0 left-0 right-0 p-6">
                      <span className="inline-block rounded-full bg-ochre px-3 py-1 text-[10px] font-medium uppercase tracking-[0.2em] text-ebony">
                        {w.category === "website" ? t.project.catWebsite : t.project.catCampaign}
                      </span>
                      <h3 className="mt-3 font-serif text-2xl font-light tracking-tight text-bone">{w.title}</h3>
                    </div>
                  </Tag>
                </Reveal>
                );
              })}
            </div>
          </div>
        </section>
      )}

      <section className="bg-bone border-t border-ebony/10">
        <Link
          to={`/${lang}/ventures/${next.slug}`}
          data-testid="project-next-link"
          className="group block max-w-7xl mx-auto px-6 lg:px-12 py-14 md:py-20"
        >
          <p className="text-xs uppercase tracking-[0.3em] text-ebony/40 mb-3">{t.project.next}</p>
          <div className="flex items-center justify-between gap-6">
            <span className="font-serif text-3xl md:text-5xl font-light tracking-tight text-ebony group-hover:text-terracotta transition-colors duration-500">
              {next.name}
            </span>
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-ebony/20 text-ebony transition-colors duration-500 group-hover:bg-terracotta group-hover:border-terracotta group-hover:text-bone">
              <ArrowRight size={18} className="transition-transform duration-500 group-hover:translate-x-0.5" />
            </span>
          </div>
        </Link>
      </section>

      <ContactForm />
    </main>
  );
}
